import { observer } from "mobx-react";
import { Dialog } from "@/components-v2/Dialog";
import { Button, IconButton, LinkButton, ToggleButton } from "@/components-v2/Button";
import { Box, VBox } from "@/components-v2/Box";
import { Sortable, SortableList } from "@/components-v2/SortableList";
import { Icon } from "@/components-v2/Icon";
import { Tag } from "@/components-v2/Tag";
import { TMessage, TMessageParam } from "@/i18n/tt";
import { DCCallback, useDispatch, useTranslation } from "@/stores/RootStoreContext";
import {
  ColumnSelectorState,
  ColumnSelectorUpdate,
  hiddenColumns,
  isDefaultSelection,
  moveColumn,
  resetColumns,
  setAllColumnsVisibility,
  setColumnVisibility,
} from "./columnSelectorDialogDomain";

export type ColumnSelectorDialogProps<Id extends string> = {
  state: ColumnSelectorState<Id>;
  getColumnLabel: (id: Id) => string | TMessage | TMessageParam;
  update: ColumnSelectorUpdate<Id>;
  onSubmit: DCCallback;
  onCancel: DCCallback;
};

export const ColumnSelectorDialog = observer(function ColumnSelectorDialog<Id extends string>({
  state,
  getColumnLabel,
  update,
  onSubmit,
  onCancel,
}: ColumnSelectorDialogProps<Id>) {
  const { t, tf } = useTranslation();
  const dispatch = useDispatch();
  const hidden = hiddenColumns(state);
  return (
    <Dialog
      title={{ key: "column-selector.title" }}
      onSubmit={onSubmit}
      onCancel={onCancel}
      submitLabel={{ key: "use" }}
      padding={"md"}
    >
      <VBox gap={"md"}>
        <Box gap={"md"} sticky bg>
          <LinkButton
            label={{ key: "column-selector.show-all" }}
            onClick={[update((s) => setAllColumnsVisibility(s, true))]}
          />
          <LinkButton
            label={{ key: "column-selector.hide-all" }}
            onClick={[update((s) => setAllColumnsVisibility(s, false))]}
          />
          <LinkButton
            label={{ key: "column-selector.reset" }}
            onClick={[update(resetColumns)]}
            disabled={isDefaultSelection(state)}
          />
        </Box>
        <SortableList
          items={state.visibleColumns.map((id) => ({ id }))}
          onMove={(from, to) => dispatch([update((s) => moveColumn(s, from.id, to.id))])}
        >
          {state.visibleColumns.map((id) => (
            <Sortable key={id} id={id}>
              {(props) => (
                <Box alignItems="center">
                  <Box
                    gap={"xl"}
                    alignItems="center"
                    htmlProps={{ ...props.dragAttributes, ...props.dragListeners }}
                    grow
                  >
                    <Icon name={"dots-six-vertical"} />
                    <Box>{tf(getColumnLabel(id))}</Box>
                  </Box>
                  <IconButton
                    icon="trash"
                    color="error"
                    tooltip={{ key: "remove" }}
                    onClick={[update((s) => setColumnVisibility(s, id, false))]}
                  />
                </Box>
              )}
            </Sortable>
          ))}
        </SortableList>
        {hidden.length > 0 && (
          <VBox gap={"md"}>
            <Box>{t("column-selector.hidden-fields")}</Box>
            <Box gap={"sm"} wrap>
              {hidden.map((id) => (
                <ToggleButton
                  key={id}
                  variant="small"
                  icon="plus"
                  label={getColumnLabel(id)}
                  selected={false}
                  onClick={[update((s) => setColumnVisibility(s, id, true))]}
                />
              ))}
            </Box>
          </VBox>
        )}
      </VBox>
    </Dialog>
  );
});

export type ColumnSelectorButtonProps = {
  hiddenCount: number;
  compact?: boolean;
  disabled?: boolean;
  onClick: DCCallback;
};

export function ColumnSelectorButton({
  hiddenCount,
  compact,
  disabled,
  onClick,
}: ColumnSelectorButtonProps) {
  const { t } = useTranslation();
  return (
    <Button
      variant={"basic"}
      fullWidth={false}
      icon={"square-split-horizontal"}
      tooltip={{ key: "column-selector.title" }}
      label={
        !compact || hiddenCount > 0 ? (
          <Box gap={"sm"} alignItems={"center"}>
            {!compact && t("column-selector.title")}
            {hiddenCount > 0 && (
              <Tag
                compact
                color={"warning"}
                className={"h-5"}
                label={{ key: "column-selector.hidden-count", params: { count: hiddenCount } }}
              />
            )}
          </Box>
        ) : undefined
      }
      disabled={disabled}
      onClick={onClick}
    />
  );
}
